import React, { useState, useEffect } from "react";
import { X, ChevronRight, ChevronLeft, Check, User, FileText, Settings2, Loader, AlertCircle, Hash, ChevronDown } from "lucide-react";


// Import existing broker forms
import { FyersUserForm } from "../../BotConfigs/FyersUsersList/FyersUserForm";
import { NubraUserForm } from "../../BotConfigs/FyersUsersList/NubraUserForm";
import { DhanHQUserForm } from "../../BotConfigs/FyersUsersList/DhanHQUserForm";
import { ZerodhaUserForm } from "../../BotConfigs/FyersUsersList/ZerodhaUserForm";
import { MOSLUserForm } from "../../BotConfigs/FyersUsersList/MOSLUserForm";
import useCRMOnboarding from "../hooks/useCRMOnboarding";
import toast from "react-hot-toast";
import apiClient from "../../../utils/apiClient";
import server from "../../../utils/serverConfig";


const STEPS = [
  { id: 1, label: "Basic Info", icon: User },
  { id: 2, label: "Legalities", icon: FileText },
  { id: 3, label: "Broker Details", icon: Settings2 },
];

const PRODUCTS = [
  { id: "optimus", label: "Optimus", dbField: "product_is_optimus" },
  { id: "pledgeplus", label: "PledgePlus", dbField: "product_is_pledgeplus" },
  { id: "polaris", label: "Polaris", dbField: "product_is_polaris" },
  { id: "polaris_lite", label: "Polaris Lite", dbField: "product_is_polaris_lite" },
];

const BROKERS = [
  {
    id: "fyers",
    label: "Fyers",
    badge: "FY",
    color: "bg-blue-600",
    ring: "ring-blue-500/40",
    border: "border-blue-500",
    text: "text-blue-400",
    bg: "bg-blue-600/10",
  },
  {
    id: "nubra",
    label: "Nubra",
    badge: "NU",
    color: "bg-purple-600",
    ring: "ring-purple-500/40",
    border: "border-purple-500",
    text: "text-purple-400",
    bg: "bg-purple-600/10",
  },
  {
    id: "dhan_hq",
    label: "DhanHQ",
    badge: "DHQ",
    color: "bg-emerald-700",
    ring: "ring-emerald-500/40",
    border: "border-emerald-500",
    text: "text-emerald-400",
    bg: "bg-emerald-600/10",
  },
  {
    id: "zerodha",
    label: "Zerodha",
    badge: "ZE",
    color: "bg-red-700",
    ring: "ring-red-500/40",
    border: "border-red-500",
    text: "text-red-400",
    bg: "bg-red-600/10",
  },
  {
    id: "mosl",
    label: "MOSL",
    badge: "MOS",
    color: "bg-amber-600",
    ring: "ring-amber-500/40",
    border: "border-amber-500",
    text: "text-amber-400",
    bg: "bg-amber-600/10",
  }
];

// ── Step Indicator ────────────────────────────────────────────────────────────
function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {STEPS.map((step, idx) => {
        const Icon = step.icon;
        const isCompleted = currentStep > step.id;
        const isActive = currentStep === step.id;

        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${isCompleted
                  ? "bg-[#07F2C7] text-black"
                  : isActive
                    ? "bg-blue-600 text-white ring-4 ring-blue-600/25"
                    : "bg-zinc-800 text-zinc-500 border border-zinc-700"
                  }`}
              >
                {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
              </div>
              <span
                className={`mt-2 text-[11px] font-medium whitespace-nowrap ${isActive ? "text-white" : isCompleted ? "text-[#07F2C7]" : "text-zinc-500"
                  }`}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-3 mb-5 transition-all duration-300 ${currentStep > step.id ? "bg-[#07F2C7]" : "bg-zinc-700"
                  }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Step 1: Basic Info ────────────────────────────────────────────────────────
function Step1BasicInfo({ data, onChange }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">
            Full Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange("name", e.target.value)}
            placeholder="Enter full name"
            className="w-full bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">
            Phone Number <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            value={data.phone_number}
            onChange={(e) => onChange("phone_number", e.target.value)}
            placeholder="+91 XXXXX XXXXX"
            className="w-full bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">
            Email Address <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            value={data.email}
            onChange={(e) => onChange("email", e.target.value)}
            placeholder="user@example.com"
            className="w-full bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">
            PAN Card Number <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={data.pan_card}
            onChange={(e) => onChange("pan_card", e.target.value.toUpperCase())}
            placeholder="ABCDE1234F"
            maxLength={10}
            className="w-full bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all font-mono tracking-widest"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">
            Source / Referral <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={data.source || ""}
            onChange={(e) => onChange("source", e.target.value)}
            placeholder="e.g. crm, google, referral"
            className="w-full bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">
            Date of Birth <span className="text-red-400">*</span>
          </label>
          <input
            type="date"
            value={data.date_of_birth || ""}
            onChange={(e) => onChange("date_of_birth", e.target.value)}
            className="w-full bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all [color-scheme:dark]"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">
            Broker <span className="text-red-400">*</span>
          </label>
          <select
            value={data.broker || ""}
            onChange={(e) => onChange("broker", e.target.value)}
            className="w-full bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all appearance-none"
          >
            <option value="" disabled>Select a broker</option>
            {BROKERS.map(b => (
              <option key={b.id} value={b.id}>{b.label}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">
            Product <span className="text-red-400">* (Select one)</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            {PRODUCTS.map((prod) => {
              const isSelected = data[prod.dbField];
              return (
                <label
                  key={prod.id}
                  className={`flex items-center gap-3 p-3.5 rounded-lg border cursor-pointer transition-all duration-200 ${isSelected
                    ? "bg-blue-900/20 border-blue-500 text-white"
                    : "bg-zinc-800/40 border-zinc-700/40 hover:border-zinc-600 text-zinc-400"
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onChange(prod.dbField, !isSelected)}
                    className="sr-only"
                  />
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${isSelected ? "bg-blue-500 border-blue-500" : "border-zinc-600 bg-transparent"
                      }`}
                  >
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <span className="text-xs font-semibold">{prod.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Legalities ────────────────────────────────────────────────────────
function Step2Legalities({ data, onChange, basicInfo, draftId, loadDraft, saveManualDocId }) {
  const [isSendingDoc, setIsSendingDoc] = React.useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = React.useState(false);

  // Manual doc ID state
  const [showManualEntry, setShowManualEntry] = React.useState(false);
  const [manualDocId, setManualDocId] = React.useState("");
  const [manualVerifyStatus, setManualVerifyStatus] = React.useState(null); // null | "pending" | "signed" | "error"
  const [isVerifying, setIsVerifying] = React.useState(false);

  const handleCheckStatus = async () => {
    if (!draftId) return;
    setIsCheckingStatus(true);
    try {
      const updatedData = await loadDraft(draftId, true);
      if (updatedData) {
        if (updatedData.is_legalities_complete) {
          toast.success("Document has been signed!");
          onChange("is_legalities_complete", true);
        } else {
          toast.success("Document signature is still pending.");
        }
      }
    } catch (err) {
      console.error("Failed to check status:", err);
      toast.error("Failed to check signature status.");
    } finally {
      setIsCheckingStatus(false);
    }
  };

  /**
   * Manual doc ID verify-and-save flow:
   * 1. Saves the doc ID to backend with is_leegality_doc_sent=true
   * 2. Refreshes the draft to read is_legalities_complete from DB
   * 3. If signed → updates local state so Continue button unlocks
   */
  const handleManualVerifyAndSave = async () => {
    if (!manualDocId.trim()) {
      toast.error("Please enter a document ID");
      return;
    }
    if (!draftId) {
      toast.error("No draft session found. Please save Step 1 first.");
      return;
    }
    setIsVerifying(true);
    setManualVerifyStatus(null);
    try {
      // Step 1: save doc ID to DB (is_leegality_doc_sent=true, is_legalities_complete=false initially)
      const saved = await saveManualDocId(draftId, manualDocId.trim(), false);
      if (!saved) {
        setManualVerifyStatus("error");
        return;
      }

      // Step 2: refresh draft to check if backend has already marked it signed
      const updatedData = await loadDraft(draftId, true);
      if (!updatedData) {
        setManualVerifyStatus("error");
        return;
      }

      if (updatedData.is_legalities_complete) {
        // Already signed — update local state so Continue unlocks
        onChange("is_leegality_doc_sent", true);
        onChange("leegality_doc_id", manualDocId.trim());
        onChange("is_legalities_complete", true);
        setManualVerifyStatus("signed");
        toast.success("Document verified as signed! You may proceed.");
      } else {
        // Doc saved but not yet signed
        onChange("is_leegality_doc_sent", true);
        onChange("leegality_doc_id", manualDocId.trim());
        setManualVerifyStatus("pending");
        toast("Document ID saved. Signature still pending.", { icon: "⏳" });
      }
    } catch (err) {
      console.error("Manual doc verify failed:", err);
      setManualVerifyStatus("error");
      toast.error("Verification failed. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };


  const handleSendLeegalityDoc = async () => {
    if (!basicInfo.name || !basicInfo.email || !basicInfo.phone_number || !basicInfo.pan_card || !basicInfo.date_of_birth || !basicInfo.broker) {
      toast.error("Please fill all required fields including Broker.");
      return;
    }
    const currentAgreementType = data.agreement_type || "authorisation";
    if (currentAgreementType === "mitc") {
      if (!data.address_line_1 || !data.address_line_1.trim()) {
        toast.error("Address Line 1 is required for the MITC agreement.");
        return;
      }
    }
    console.log("Draft ID_-----", draftId);
    setIsSendingDoc(true);
    try {
      const response = await apiClient.request({
        method: "post",
        url: `/crm/onboarding/${draftId}/send-docuseal`,
        data: {
          agreement_type: currentAgreementType,
          address_line_1: (data.address_line_1 || "").trim(),
          address_line_2: (data.address_line_2 || "").trim(),
        },
        headers: { "Content-Type": "application/json" }
      });
      const docId = response.data?.leegality_doc_id || response.data?.doc_id || response.data?.document_id || response.data?.id || "";
      onChange("is_leegality_doc_sent", true);
      if (docId) onChange("leegality_doc_id", docId);
      toast.success("DocuSeal document sent successfully!");
    } catch (err) {
      console.error("Failed to send DocuSeal document:", err);
      toast.error("Failed to send DocuSeal document. Please try again.");
    } finally {
      setIsSendingDoc(false);
    }
  };

  const docSent = data.is_leegality_doc_sent;
  const docSigned = data.is_legalities_complete;

  if (docSigned) {
    return (
      <div className="space-y-6">
        <div className="p-5 bg-emerald-950/30 border border-emerald-500/20 rounded-xl flex items-start gap-4">
          <div className="w-8 h-8 rounded-full bg-[#07F2C7]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Check className="w-4 h-4 text-[#07F2C7]" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white">Compliance Completed</h4>
            <p className="text-zinc-400 text-xs mt-1">
              The {data.agreement_type === "mitc" ? "MITC Terms & Conditions Agreement" : "Authorisation Agreement"} is signed and all compliance checks are completed.
            </p>
            {data.leegality_doc_id && (
              <p className="text-zinc-500 text-[11px] mt-2">
                Document ID: <span className="font-mono text-zinc-300 bg-zinc-900/60 px-2 py-0.5 rounded border border-zinc-800/40">{data.leegality_doc_id}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {docSent ? (
        /* Agreement Status Card */
        <div className="space-y-3">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Agreement &amp; Signature</p>

          <div className="p-5 bg-zinc-800/40 border border-zinc-700/40 rounded-xl space-y-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-white">
                  {data.agreement_type === "mitc" ? "MITC Terms & Conditions Agreement" : "Authorisation Letter"}
                </h4>
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${
                  data.agreement_type === "mitc"
                    ? "bg-purple-500/10 text-purple-400 border-purple-500/20"
                    : "bg-blue-500/10 text-blue-400 border-blue-500/20"
                }`}>
                  {data.agreement_type === "mitc" ? "B2B / Partner" : "B2C / Default"}
                </span>
              </div>
              {docSigned ? (
                <p className="text-emerald-400 text-xs font-semibold flex items-center gap-1.5 mt-2">
                  <Check className="w-4 h-4" />
                  Document signed — ready to proceed
                </p>
              ) : (
                <p className="text-amber-400 text-xs font-medium flex items-center gap-1.5 mt-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                  The Document Signing is sent waiting for Client to sign in
                </p>
              )}
            </div>

            <div className="pt-3 border-t border-zinc-700/40 flex items-center justify-between gap-4">
              {data.leegality_doc_id ? (
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 text-xs">Doc ID:</span>
                  <span className="font-mono text-xs text-zinc-300 bg-zinc-900/60 px-2 py-0.5 rounded border border-zinc-700/50">
                    {data.leegality_doc_id}
                  </span>
                </div>
              ) : (
                <div />
              )}
              <button
                type="button"
                onClick={handleCheckStatus}
                disabled={isCheckingStatus}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white border border-zinc-700/60 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingStatus ? (
                  <>
                    <Loader className="w-3.5 h-3.5 animate-spin" />
                    Checking...
                  </>
                ) : (
                  "Check Status"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Agreement Selection */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Agreement Document Selection</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Authorisation Option */}
              <div
                onClick={() => onChange("agreement_type", "authorisation")}
                className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                  (data.agreement_type || "authorisation") === "authorisation"
                    ? "bg-blue-900/20 border-blue-500 ring-1 ring-blue-500/30"
                    : "bg-zinc-800/40 border-zinc-700/40 hover:border-zinc-600"
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-semibold text-white">Authorisation Letter</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    B2C / Default
                  </span>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Standard client authorization agreement for direct retail accounts.
                </p>
              </div>

              {/* MITC Option */}
              <div
                onClick={() => onChange("agreement_type", "mitc")}
                className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                  data.agreement_type === "mitc"
                    ? "bg-purple-900/20 border-purple-500 ring-1 ring-purple-500/30"
                    : "bg-zinc-800/40 border-zinc-700/40 hover:border-zinc-600"
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-semibold text-white">MITC Agreement</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    B2B / Partner
                  </span>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Most Important Terms &amp; Conditions (when a broker or partner is in the middle).
                </p>
              </div>
            </div>

            {/* Address Section (Only for MITC) */}
            {data.agreement_type === "mitc" && (
              <div className="p-4 bg-zinc-900/70 border border-purple-500/30 rounded-xl space-y-3.5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-purple-400" />
                  <h5 className="text-xs font-semibold text-purple-300 uppercase tracking-wider">
                    Client / Entity Address (Required for MITC)
                  </h5>
                </div>
                <p className="text-zinc-400 text-xs">
                  This address will be automatically pre-filled into the MITC agreement document before sending.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-zinc-300 mb-1">
                      Address Line 1 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={data.address_line_1 || ""}
                      onChange={(e) => onChange("address_line_1", e.target.value)}
                      placeholder="Flat / Building / Street / Landmark"
                      className="w-full bg-zinc-800/80 border border-zinc-700 rounded-lg px-3.5 py-2 text-white text-xs placeholder-zinc-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-300 mb-1">
                      Address Line 2 <span className="text-zinc-500">(City, State, PIN)</span>
                    </label>
                    <input
                      type="text"
                      value={data.address_line_2 || ""}
                      onChange={(e) => onChange("address_line_2", e.target.value)}
                      placeholder="Locality, City, State, PIN Code"
                      className="w-full bg-zinc-800/80 border border-zinc-700 rounded-lg px-3.5 py-2 text-white text-xs placeholder-zinc-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 transition-all"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Info banner */}
          <div className="p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-amber-400 text-xs font-bold">!</span>
              </div>
              <p className="text-amber-400/90 text-xs leading-relaxed">
                Please review and confirm the agreement before proceeding to broker setup.
                The client must sign the document to continue.
              </p>
            </div>
          </div>

          {/* Single consent checkbox */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Compliance Consent</p>
            <div
              onClick={() => onChange("consent_accepted", !data.consent_accepted)}
              className={`flex items-start gap-3.5 p-3.5 rounded-lg border cursor-pointer transition-all duration-200 ${data.consent_accepted
                ? "bg-emerald-900/20 border-emerald-700/40"
                : "bg-zinc-800/40 border-zinc-700/40 hover:border-zinc-600"
                }`}
            >
              <div
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${data.consent_accepted ? "bg-emerald-500 border-emerald-500" : "border-zinc-600 bg-transparent"
                  }`}
              >
                {data.consent_accepted && <Check className="w-3 h-3 text-black" />}
              </div>
              <div>
                <span className={`text-sm ${data.consent_accepted ? "text-white" : "text-zinc-400"}`}>
                  I confirm that the client has agreed to the Terms &amp; Conditions, Risk Disclosure, and Data Consent.
                </span>
                <span className="ml-1.5 text-[10px] text-red-400 font-medium">Required</span>
              </div>
            </div>
          </div>

          {/* Investment Advisory Agreement Section */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Agreement &amp; Signature</p>

            <div className="p-5 bg-zinc-800/40 border border-zinc-700/40 rounded-xl space-y-4">
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-white">
                  {data.agreement_type === "mitc" ? "MITC Terms & Conditions Agreement" : "Authorisation Letter"}
                </h4>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  {data.agreement_type === "mitc"
                    ? "Please review and send the B2B MITC Terms & Conditions agreement to the client for signature."
                    : "Please review and send the client Authorisation agreement to proceed with onboarding."}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSendLeegalityDoc}
                  disabled={isSendingDoc || !data.consent_accepted}
                  className="px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/15 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!data.consent_accepted ? "Please accept the compliance consent first" : ""}
                >
                  {isSendingDoc ? (
                    <span className="flex items-center gap-1.5">
                      <Loader className="w-3.5 h-3.5 animate-spin" />
                      Signing...
                    </span>
                  ) : (
                    "Sign"
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* On-Hold Option */}
      <div className="space-y-3 pt-4 border-t border-zinc-800/60">
        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Bypass / On-Hold Configuration</p>
        <div
          onClick={() => onChange("is_onhold_leegality", !data.is_onhold_leegality)}
          className={`flex items-start gap-3.5 p-3.5 rounded-lg border cursor-pointer transition-all duration-200 ${data.is_onhold_leegality
            ? "bg-amber-900/10 border-amber-500/40"
            : "bg-zinc-800/40 border-zinc-700/40 hover:border-zinc-600"
            }`}
        >
          <div
            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${data.is_onhold_leegality ? "bg-amber-500 border-amber-500 text-black" : "border-zinc-600 bg-transparent"
              }`}
          >
            {data.is_onhold_leegality && <Check className="w-3 h-3 text-black stroke-[3]" />}
          </div>
          <div>
            <span className={`text-sm ${data.is_onhold_leegality ? "text-white" : "text-zinc-400"}`}>
              Put Legality On-Hold (Proceed directly to Step 3 to configure broker details)
            </span>
            <p className="text-zinc-500 text-xs mt-1">
              Enable this if you have the client's broker details and wish to configure them now. The onboarding will remain on hold until the agreement is signed.
            </p>
          </div>
        </div>
      </div>

      {/* ── Manual Document ID Entry ── */}
      {!docSigned && (
        <div className="pt-4 border-t border-zinc-800/60">
          <button
            type="button"
            onClick={() => {
              setShowManualEntry((v) => !v);
              setManualVerifyStatus(null);
            }}
            className="w-full flex items-center justify-between gap-2 text-xs font-semibold text-amber-400/80 hover:text-amber-300 transition-colors group"
          >
            <span className="flex items-center gap-2">
              <Hash className="w-3.5 h-3.5" />
              Have an existing DocuSeal submission ID? Enter it manually
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-200 ${
                showManualEntry ? "rotate-180" : ""
              }`}
            />
          </button>

          {showManualEntry && (
            <div className="mt-3 p-4 bg-amber-950/15 border border-amber-700/25 rounded-xl space-y-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-zinc-400">
                  DocuSeal Document ID
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={manualDocId}
                    onChange={(e) => {
                      setManualDocId(e.target.value);
                      setManualVerifyStatus(null);
                    }}
                    placeholder="e.g. DOC-XXXXXXXX or UUID"
                    className="flex-1 bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-zinc-600 font-mono focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/20 transition-all"
                  />
                  <button
                    type="button"
                    onClick={handleManualVerifyAndSave}
                    disabled={isVerifying || !manualDocId.trim()}
                    className="px-4 py-2.5 rounded-lg text-xs font-semibold bg-amber-600/80 hover:bg-amber-500/80 text-white border border-amber-500/40 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isVerifying ? (
                      <>
                        <Loader className="w-3.5 h-3.5 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Verify &amp; Save
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Status display */}
              {manualVerifyStatus === "signed" && (
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-emerald-950/40 border border-emerald-500/25">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-emerald-400" />
                  </div>
                  <p className="text-emerald-400 text-xs font-medium">
                    Document is <strong>signed</strong>! Legality step is complete — you may now proceed to Step 3.
                  </p>
                </div>
              )}

              {manualVerifyStatus === "pending" && (
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-amber-950/30 border border-amber-600/25">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                  <p className="text-amber-400/90 text-xs font-medium">
                    Document ID saved. The client has <strong>not signed</strong> yet. Check again later using “Check Status” above.
                  </p>
                </div>
              )}

              {manualVerifyStatus === "error" && (
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-red-950/30 border border-red-700/25">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-xs font-medium">
                    Could not save or verify the document ID. Please check the ID and try again.
                  </p>
                </div>
              )}

              {/* ── Force Mark as Signed ── */}
              <div className="pt-3 border-t border-amber-800/30 space-y-2">
                <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Manual Override</p>
                <ForceMarkSignedToggle
                  draftId={draftId}
                  manualDocId={manualDocId}
                  saveManualDocId={saveManualDocId}
                  onChange={onChange}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Force Mark Signed — inner component with confirm guard ────────────────────
function ForceMarkSignedToggle({ draftId, manualDocId, saveManualDocId, onChange }) {
  const [confirmed, setConfirmed] = React.useState(false);
  const [isMarking, setIsMarking] = React.useState(false);

  const handleForceComplete = async () => {
    if (!draftId) {
      toast.error("No draft session found. Please save Step 1 first.");
      return;
    }
    setIsMarking(true);
    try {
      // Save doc ID (if any) + mark both flags true in DB
      const docIdToUse = manualDocId.trim() || "manual-override";
      const saved = await saveManualDocId(draftId, docIdToUse, true);
      if (saved) {
        // Immediately update local state so Continue unlocks
        onChange("is_leegality_doc_sent", true);
        onChange("leegality_doc_id", docIdToUse);
        onChange("is_legalities_complete", true);
        toast.success("Legality manually marked as complete.");
      }
    } catch (err) {
      console.error("Force mark failed:", err);
      toast.error("Failed to mark legality as complete.");
    } finally {
      setIsMarking(false);
    }
  };

  return (
    <div className="space-y-2.5">
      {/* Confirm toggle */}
      <div
        onClick={() => setConfirmed((v) => !v)}
        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
          confirmed
            ? "bg-rose-950/20 border-rose-700/30"
            : "bg-zinc-800/30 border-zinc-700/30 hover:border-zinc-600"
        }`}
      >
        <div
          className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
            confirmed ? "bg-rose-500 border-rose-500" : "border-zinc-600 bg-transparent"
          }`}
        >
          {confirmed && <Check className="w-2.5 h-2.5 text-white stroke-[3]" />}
        </div>
        <span className={`text-xs leading-relaxed ${confirmed ? "text-rose-300" : "text-zinc-500"}`}>
          I confirm I want to <strong>manually mark this legality as complete</strong> without waiting for DocuSeal signature.
        </span>
      </div>

      {/* Mark complete button — only active after confirm */}
      <button
        type="button"
        onClick={handleForceComplete}
        disabled={!confirmed || isMarking}
        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold bg-rose-700/60 hover:bg-rose-600/70 text-white border border-rose-600/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {isMarking ? (
          <>
            <Loader className="w-3.5 h-3.5 animate-spin" />
            Marking...
          </>
        ) : (
          <>
            <Check className="w-3.5 h-3.5" />
            Force Mark Legality as Complete
          </>
        )}
      </button>
    </div>
  );
}




// ── Step 3: Broker Selection + Broker Form ────────────────────────────────────
function Step3BrokerDetails({
  selectedBroker,
  onBrokerChange,
  allowedBrokers,
  proxyData,
  handleBrokerSuccess,
  onWizardSubmit,
  onClose,
  isOnholdLegality,
  isLegalitiesComplete,
  isBrokerAdded,
  loadedClientId,
  isPolarisOrPolarisLite,
  basicInfo,
  draftData
}) {
  const activeBrokerInfo = BROKERS.find((b) => b.id === selectedBroker);

  if (isBrokerAdded) {
    return (
      <div className="space-y-6 font-inter">
        <div className="p-5 bg-zinc-800/40 border border-zinc-700/40 rounded-xl space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-[#07F2C7]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-4 h-4 text-[#07F2C7]" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white font-inter">Broker Configured</h4>
              <p className="text-zinc-400 text-xs mt-1">
                The account has been successfully configured.
              </p>
            </div>
          </div>
          
          <div className="pt-3 border-t border-zinc-700/40 grid grid-cols-2 gap-4">
            <div>
              <span className="text-zinc-500 text-xs block">Broker</span>
              <span className="text-sm font-semibold text-white uppercase">{selectedBroker || "-"}</span>
            </div>
            <div>
              <span className="text-zinc-500 text-xs block">Client ID</span>
              <span className="text-sm font-mono text-white">{loadedClientId || "-"}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Broker Selector */}
      <div>
        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">
          Select Broker <span className="text-red-400">*</span>
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {BROKERS.filter(b => !basicInfo?.broker || b.id === basicInfo.broker).map((broker) => {
            const isAllowed = allowedBrokers.includes(broker.id);
            const isSelected = selectedBroker === broker.id;

            return (
              <button
                key={broker.id}
                type="button"
                disabled={!isAllowed}
                onClick={() => onBrokerChange(broker.id)}
                className={`relative flex flex-col items-center gap-1.5 py-2 px-3 rounded-lg border-2 transition-all duration-200 group ${isSelected
                  ? `${broker.border} ${broker.bg} ring-4 ${broker.ring}`
                  : isAllowed
                    ? "border-zinc-700/60 bg-zinc-800/40 hover:border-zinc-600 hover:bg-zinc-800/70"
                    : "border-zinc-800/40 bg-zinc-900/10 opacity-30 cursor-not-allowed"
                  }`}
                title={!isAllowed ? "Not allowed for selected product(s)" : ""}
              >
                {/* Selected checkmark */}
                {isSelected && (
                  <div className={`absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full ${broker.color} flex items-center justify-center`}>
                    <Check className="w-2 h-2 text-white" />
                  </div>
                )}

                {/* Badge */}
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white transition-all duration-200 ${isSelected ? broker.color : isAllowed ? "bg-zinc-700/60 group-hover:bg-zinc-700" : "bg-zinc-800"
                    }`}
                >
                  {broker.badge}
                </div>

                <span
                  className={`text-[11px] font-semibold transition-colors ${isSelected ? broker.text : isAllowed ? "text-zinc-400 group-hover:text-zinc-200" : "text-zinc-600"
                    }`}
                >
                  {broker.label}
                </span>

                {!isAllowed && (
                  <span className="absolute bottom-0.5 text-[7px] text-zinc-500 font-semibold uppercase">
                    Unavailable
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      {selectedBroker && (
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-xs text-zinc-500 font-medium px-2">
            {activeBrokerInfo?.label} Account Details
          </span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>
      )}

      {/* Broker Form */}
      {selectedBroker && (
        <div className="pr-1">
          {selectedBroker === "fyers" && (
            <FyersUserForm
              handleAddFyersUserIndexMuliplier={(clientId, name, mult, bn_mult, fi_mult, mc_mult, sx_mult) => {
                handleBrokerSuccess("fyers", clientId, name, {
                  totp_key: "",
                  pin: "",
                  status: (isOnholdLegality && !isLegalitiesComplete) ? "inactive" : "active",
                  multiplier: mult,
                  banknifty_multiplier: bn_mult,
                  finnifty_multiplier: fi_mult,
                  midcpnifty_multiplier: mc_mult,
                  sensex_multiplier: sx_mult
                });
              }}
              onClose={(success) => {
                if (!success) toast.error("Fyers setup canceled");
              }}
              proxyData={proxyData}
              isOnholdLegality={isOnholdLegality}
              isLegalitiesComplete={isLegalitiesComplete}
              isPolarisOrPolarisLite={isPolarisOrPolarisLite}
              isWizard={true}
              onWizardSubmit={({ client_id, brokerData }) => {
                onWizardSubmit("fyers", client_id, brokerData);
              }}
              prefilledName={basicInfo?.name}
              prefilledPhone={basicInfo?.phone_number}
              prefilledClientId={draftData?.client_id || draftData?.fyers_client_id}
              prefilledTotpKey={draftData?.fyers_totp_key}
              prefilledPin={draftData?.fyers_pin}
              prefilledAppId={draftData?.fyers_app_id}
              prefilledAppType={draftData?.fyers_app_type}
              prefilledAppSecret={draftData?.fyers_app_secret}
              prefilledRedirectUrl={draftData?.fyers_redirect_url}
              prefilledAppIdType={draftData?.fyers_app_id_type}
              prefilledLabel={draftData?.fyers_label}
              prefilledProxyUser={draftData?.fyers_proxy_user}
              prefilledProxyPassword={draftData?.fyers_proxy_password}
              prefilledProxyHost={draftData?.fyers_proxy_host}
              prefilledProxyPort={draftData?.fyers_proxy_port}
              prefilledProxyStaticIp={draftData?.fyers_proxy_static_ip}
              prefilledProxyToggle={draftData?.fyers_proxy_toggle}
            />
          )}
          {selectedBroker === "nubra" && (
            <NubraUserForm
              handleAddNubraUserIndexMuliplier={(clientId, name, mult, bn_mult, fi_mult, mc_mult, sx_mult) => {
                handleBrokerSuccess("nubra", clientId, name, {
                  totp_key: "",
                  pin: "",
                  status: (isOnholdLegality && !isLegalitiesComplete) ? "inactive" : "active",
                  multiplier: mult,
                  banknifty_multiplier: bn_mult,
                  finnifty_multiplier: fi_mult,
                  midcpnifty_multiplier: mc_mult,
                  sensex_multiplier: sx_mult
                });
              }}
              onClose={(success) => {
                if (!success) toast.error("Nubra setup canceled");
              }}
              proxyData={proxyData}
              isOnholdLegality={isOnholdLegality}
              isLegalitiesComplete={isLegalitiesComplete}
              isPolarisOrPolarisLite={isPolarisOrPolarisLite}
              isWizard={true}
              onWizardSubmit={({ client_id, brokerData }) => {
                onWizardSubmit("nubra", client_id, brokerData);
              }}
              prefilledName={basicInfo?.name}
              prefilledPhone={basicInfo?.phone_number}
              prefilledEmail={basicInfo?.email}
              prefilledDob={basicInfo?.date_of_birth}
              prefilledClientId={draftData?.client_id || draftData?.nubra_client_id}
              prefilledTotpKey={draftData?.nubra_totp_key}
              prefilledPin={draftData?.nubra_pin}
              prefilledPassword={draftData?.nubra_password}
              prefilledLabel={draftData?.nubra_label}
              prefilledSizing={draftData?.nubra_sizing}
              prefilledAccountStatus={draftData?.nubra_account_status}
              prefilledProxyUser={draftData?.nubra_proxy_user}
              prefilledProxyPassword={draftData?.nubra_proxy_password}
              prefilledProxyHost={draftData?.nubra_proxy_host}
              prefilledProxyPort={draftData?.nubra_proxy_port}
              prefilledProxyStaticIp={draftData?.nubra_proxy_static_ip}
              prefilledProxyToggle={draftData?.nubra_proxy_toggle}
            />
          )}
          {selectedBroker === "dhan_hq" && (
            <DhanHQUserForm
              handleAddDhanHQUserIndexMultiplier={(clientId, name, mult, bn_mult, fi_mult, mc_mult, sx_mult) => {
                handleBrokerSuccess("dhan_hq", clientId, name, {
                  status: (isOnholdLegality && !isLegalitiesComplete) ? "inactive" : "active",
                  multiplier: mult,
                  banknifty_multiplier: bn_mult,
                  finnifty_multiplier: fi_mult,
                  midcpnifty_multiplier: mc_mult,
                  sensex_multiplier: sx_mult
                });
              }}
              onClose={(success) => {
                if (!success) toast.error("DhanHQ setup canceled");
              }}
              proxyData={proxyData}
              isOnholdLegality={isOnholdLegality}
              isLegalitiesComplete={isLegalitiesComplete}
              isPolarisOrPolarisLite={isPolarisOrPolarisLite}
              isWizard={true}
              onWizardSubmit={({ client_id, brokerData }) => {
                onWizardSubmit("dhan_hq", client_id, brokerData);
              }}
              prefilledName={basicInfo?.name}
              prefilledPhone={basicInfo?.phone_number}
              prefilledEmail={basicInfo?.email}
              prefilledDob={basicInfo?.date_of_birth}
              prefilledClientId={draftData?.client_id || draftData?.dhan_hq_client_id}
              prefilledTotpKey={draftData?.dhan_hq_totp_key}
              prefilledPin={draftData?.dhan_hq_pin}
              prefilledRedirectUrl={draftData?.dhan_hq_redirect_url}
              prefilledPassword={draftData?.dhan_hq_password}
              prefilledApiKey={draftData?.dhan_hq_api_key}
              prefilledApiSecret={draftData?.dhan_hq_api_secret}
              prefilledSizing={draftData?.dhan_hq_sizing}
              prefilledAccountStatus={draftData?.dhan_hq_account_status}
              prefilledLabel={draftData?.dhan_hq_label}
              prefilledProxyUser={draftData?.dhan_hq_proxy_user}
              prefilledProxyPassword={draftData?.dhan_hq_proxy_password}
              prefilledProxyHost={draftData?.dhan_hq_proxy_host}
              prefilledProxyPort={draftData?.dhan_hq_proxy_port}
              prefilledProxyStaticIp={draftData?.dhan_hq_proxy_static_ip}
              prefilledProxyToggle={draftData?.dhan_hq_proxy_toggle}
            />
          )}
          {selectedBroker === "zerodha" && (
            <ZerodhaUserForm
              handleAddUserIndexMultiplier={(clientId, name, mult, bn_mult, fi_mult, mc_mult, sx_mult, extraFields) => {
                handleBrokerSuccess("zerodha", clientId, name, {
                  status: (isOnholdLegality && !isLegalitiesComplete) ? "inactive" : "active",
                  multiplier: mult,
                  banknifty_multiplier: bn_mult,
                  finnifty_multiplier: fi_mult,
                  midcpnifty_multiplier: mc_mult,
                  sensex_multiplier: sx_mult,
                  ...extraFields
                });
              }}
              onClose={(success) => {
                if (!success) toast.error("Zerodha setup canceled");
              }}
              proxyData={proxyData}
              isOnholdLegality={isOnholdLegality}
              isLegalitiesComplete={isLegalitiesComplete}
              isWizard={true}
              onWizardSubmit={({ client_id, brokerData }) => {
                onWizardSubmit("zerodha", client_id, brokerData);
              }}
              prefilledName={basicInfo?.name}
              prefilledPhone={basicInfo?.phone_number}
              prefilledEmail={basicInfo?.email}
              prefilledClientId={draftData?.client_id || draftData?.zerodha_client_id}
              prefilledTotpKey={draftData?.zerodha_totp_key}
              prefilledPassword={draftData?.zerodha_password || draftData?.zerodha_pin}
              prefilledApiKey={draftData?.zerodha_api_key}
              prefilledApiSecretKey={draftData?.zerodha_api_secret || draftData?.zerodha_api_secret_key}
              prefilledSizing={draftData?.zerodha_sizing}
              prefilledProxyUser={draftData?.zerodha_proxy_user}
              prefilledProxyPassword={draftData?.zerodha_proxy_password}
              prefilledProxyHost={draftData?.zerodha_proxy_host}
              prefilledProxyPort={draftData?.zerodha_proxy_port}
              prefilledProxyStaticIp={draftData?.zerodha_proxy_static_ip}
              prefilledProxyToggle={draftData?.zerodha_proxy_toggle}
            />
          )}
          {selectedBroker === "mosl" && (
            <MOSLUserForm
              handleAddUserIndexMultiplier={(clientId, name, mult, bn_mult, fi_mult, mc_mult, sx_mult, extraFields) => {
                handleBrokerSuccess("mosl", clientId, name, {
                  status: (isOnholdLegality && !isLegalitiesComplete) ? "inactive" : "active",
                  multiplier: mult,
                  banknifty_multiplier: bn_mult,
                  finnifty_multiplier: fi_mult,
                  midcpnifty_multiplier: mc_mult,
                  sensex_multiplier: sx_mult,
                  ...extraFields
                });
              }}
              onClose={(success) => {
                if (!success) toast.error("MOSL setup canceled");
              }}
              proxyData={proxyData}
              isOnholdLegality={isOnholdLegality}
              isLegalitiesComplete={isLegalitiesComplete}
              isWizard={true}
              onWizardSubmit={({ client_id, brokerData }) => {
                onWizardSubmit("mosl", client_id, brokerData);
              }}
              prefilledName={basicInfo?.name}
              prefilledPhone={basicInfo?.phone_number}
              prefilledEmail={basicInfo?.email}
              prefilledDob={draftData?.mosl_dob || basicInfo?.date_of_birth}
              prefilledClientId={draftData?.client_id || draftData?.mosl_client_id}
              prefilledTotpKey={draftData?.mosl_totp_key}
              prefilledPassword={draftData?.mosl_password}
              prefilledApiKey={draftData?.mosl_api_key}
              prefilledApiSecretKey={draftData?.mosl_api_secret || draftData?.mosl_api_secret_key}
              prefilledSizing={draftData?.mosl_sizing}
              prefilledProxyUser={draftData?.mosl_proxy_user}
              prefilledProxyPassword={draftData?.mosl_proxy_password}
              prefilledProxyHost={draftData?.mosl_proxy_host}
              prefilledProxyPort={draftData?.mosl_proxy_port}
              prefilledProxyStaticIp={draftData?.mosl_proxy_static_ip}
              prefilledProxyToggle={draftData?.mosl_proxy_toggle}
            />
          )}
        </div>
      )}

      {/* Placeholder when no broker selected */}
      {!selectedBroker && (
        <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-zinc-800 rounded-xl">
          <div className="w-12 h-12 rounded-full bg-zinc-800/60 flex items-center justify-center mb-3">
            <Settings2 className="w-5 h-5 text-zinc-600" />
          </div>
          <p className="text-zinc-400 text-sm font-medium">Choose a broker above</p>
          <p className="text-zinc-600 text-xs mt-1">The broker-specific form will appear here</p>
        </div>
      )}
    </div>
  );
}

// ── Main Wizard ───────────────────────────────────────────────────────────────
export default function AddUserWizard({
  isOpen,
  onClose,
  resumeDraftId = null,
  proxyData: propProxyData = null,
  handleAddFyers,
  handleAddNubra,
  handleAddDhanHQ,
  handleAddZerodha,
  handleAddMOSL,
}) {
  const {
    draftId,
    currentStep,
    setCurrentStep,
    isSaving,
    basicInfo,
    setBasicInfo,
    legalities,
    setLegalities,
    selectedBroker,
    setSelectedBroker,
    allowedBrokers,
    resetOnboarding,
    createDraft,
    saveBasicInfo,
    saveLegalities,
    saveBrokerDetails,
    saveManualDocId,
    loadDraft,
    fetchAllowedBrokers,
  } = useCRMOnboarding();


  const [proxyData, setProxyData] = useState(null);
  const proxyDataRef = React.useRef(null);
  const [loadedClientId, setLoadedClientId] = useState("");
  const [isBrokerAdded, setIsBrokerAdded] = useState(false);
  const [draftData, setDraftData] = useState(null);

  const claimProxy = async () => {
    if (proxyDataRef.current) return;
    if (draftData?.is_already_ip) {
      console.log("is_already_ip is true, skipping proxy claim");
      return;
    }
    try {
      const response = await apiClient.request({
        method: "get",
        url: `${server.godseye.baseUrl}proxies/next`,
      });
      if (response.data) {
        setProxyData(response.data);
        proxyDataRef.current = response.data;
      }
    } catch (error) {
      console.error("Failed to claim proxy:", error);
    }
  };

  const releaseProxy = async () => {
    const current = proxyDataRef.current;
    if (current) {
      try {
        await apiClient.request({
          method: "post",
          url: `${server.godseye.baseUrl}proxies/release`,
          data: { proxy_static_ip: current.proxy_static_ip },
        });
      } catch (err) {
        console.error("Failed to release proxy:", err);
      }
      setProxyData(null);
      proxyDataRef.current = null;
    }
  };

  // Sync propProxyData to local state if provided by parent.
  // IMPORTANT: when propProxyData becomes null (e.g. parent committed & cleared it),
  // we MUST also null the wizard's ref.  Without this the proxy-management effect's
  // cleanup fires with the stale IP still in proxyDataRef.current and sends a
  // spurious /proxies/release immediately after the parent's /proxies/commit.
  useEffect(() => {
    if (propProxyData) {
      setProxyData(propProxyData);
      proxyDataRef.current = propProxyData;
    } else {
      // Parent cleared the proxy (committed or released on its side) — mirror it.
      setProxyData(null);
      proxyDataRef.current = null;
    }
  }, [propProxyData]);

  // Proxy management on step 3 (only if not supplied by parent).
  // NOTE: Only claim in the effect body; release is handled solely by the
  // cleanup function. Having BOTH an else-body release AND a cleanup release
  // caused a double-release when deps like currentStep changed (e.g. after
  // resetOnboarding sets step back to 1) because the cleanup fires first
  // (async, ref still set) and then the body fires again immediately.
  //
  // IMPORTANT: propProxyData is intentionally NOT in the dep array below.
  // When propProxyData changes {ip}→null (parent committed), React runs ALL
  // effect cleanups before running any effect bodies. That means this
  // cleanup fires BEFORE the sync effect (above) gets a chance to null
  // proxyDataRef. The stale IP would be read by releaseProxy → spurious
  // /proxies/release. Removing propProxyData from the dep array prevents
  // this cleanup from being triggered by the parent's commit.
  // The `if (propProxyData) return` guard in the body still keeps the
  // claim logic correct on initial setup and tab changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (propProxyData) return;

    // If we are resuming a draft, wait until draftData is loaded
    if (resumeDraftId && !draftData) return;

    if (isOpen && currentStep === 3) {
      claimProxy();
    }
    // No else-branch release here — the cleanup below handles it.

    return () => {
      releaseProxy();
    };
  }, [isOpen, currentStep, resumeDraftId, draftData]); // propProxyData intentionally excluded — see comment above

  // Proxy heartbeat (only if not supplied by parent)
  useEffect(() => {
    if (propProxyData || !proxyData) return;
    const interval = setInterval(async () => {
      try {
        await apiClient.request({
          method: "post",
          url: `${server.godseye.baseUrl}proxies/heartbeat`,
          data: { proxy_static_ip: proxyData.proxy_static_ip },
        });
      } catch (err) {
        console.error("Heartbeat failed", err);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [proxyData, propProxyData]);

  // Load draft if resuming
  useEffect(() => {
    if (isOpen) {
      resetOnboarding();
      setLoadedClientId("");
      setIsBrokerAdded(false);
      setDraftData(null);
      if (resumeDraftId) {
        loadDraft(resumeDraftId).then((data) => {
          if (data) {
            setDraftData(data);
            if (data.client_id) setLoadedClientId(data.client_id);
            if (data.is_broker_added) setIsBrokerAdded(true);
          }
        });
      }
    }
  }, [isOpen, resumeDraftId, loadDraft, resetOnboarding]);

  if (!isOpen) return null;

  const handleBasicInfoChange = (field, value) => {
    setBasicInfo((prev) => {
      if (field.startsWith("product_is_")) {
        const updated = {
          ...prev,
          product_is_optimus: false,
          product_is_pledgeplus: false,
          product_is_polaris: false,
          product_is_polaris_lite: false,
        };
        if (value) {
          updated[field] = true;
        }
        return updated;
      }
      return { ...prev, [field]: value };
    });
  };

  const handleLegalitiesChange = (field, value) => {
    setLegalities((prev) => ({ ...prev, [field]: value }));
  };

  const isStep1Valid =
    basicInfo.name &&
    basicInfo.phone_number &&
    basicInfo.email &&
    basicInfo.pan_card &&
    basicInfo.date_of_birth &&
    basicInfo.broker &&
    basicInfo.source &&
    (basicInfo.product_is_optimus ||
      basicInfo.product_is_pledgeplus ||
      basicInfo.product_is_polaris ||
      basicInfo.product_is_polaris_lite);

  const isStep2Valid =
    (legalities.is_leegality_doc_sent &&
      legalities.is_legalities_complete) ||
    legalities.is_onhold_leegality;

  // Navigations
  const handleNext = async () => {
    if (currentStep === 1) {
      const success = await saveBasicInfo(draftId, basicInfo);
      if (success) {
        const activeProducts = [];
        if (basicInfo.product_is_optimus) activeProducts.push("optimus");
        if (basicInfo.product_is_pledgeplus) activeProducts.push("pledgeplus");
        if (basicInfo.product_is_polaris) activeProducts.push("polaris");
        if (basicInfo.product_is_polaris_lite) activeProducts.push("polaris_lite");
        await fetchAllowedBrokers(activeProducts);

        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      const success = await saveLegalities(draftId, legalities);
      if (success) {
        setCurrentStep(3);
      }
    }
  };

  const handleClose = async () => {
    if (currentStep === 1) {
      if (basicInfo.name || basicInfo.email) {
        await saveBasicInfo(draftId, basicInfo);
      }
    } else if (currentStep === 2 && draftId) {
      await saveLegalities(draftId, legalities);
    }
    resetOnboarding();
    onClose(false);
  };

  const handleWizardSubmitForApproval = async (brokerName, clientId, brokerFields) => {
    const success = await saveBrokerDetails(draftId, brokerName, clientId, brokerFields);
    if (success) {
      setIsBrokerAdded(true);
      setLoadedClientId(clientId);
      // If the wizard itself claimed the proxy (no propProxyData provided), commit it here.
      // IMPORTANT: capture the IP and NULL the ref BEFORE the await so that any
      // React re-render triggered during the async gap finds the ref already empty
      // and the proxy useEffect cleanup is a no-op (preventing accidental release).
      if (!propProxyData && proxyDataRef.current) {
        const proxyIp = proxyDataRef.current.proxy_static_ip;
        setProxyData(null);
        proxyDataRef.current = null; // clear ref before await
        try {
          await apiClient.request({
            method: "post",
            url: `${server.godseye.baseUrl}proxies/commit`,
            data: { proxy_static_ip: proxyIp }
          });
        } catch (err) {
          console.error("Failed to commit proxy inside wizard:", err);
        }
      }

      toast.success(`${brokerName.toUpperCase()} user submitted for approval!`);
      resetOnboarding();
      onClose(true);
    }
  };

  const handleBrokerSuccess = async (brokerName, clientId, name, brokerFields) => {
    const success = await saveBrokerDetails(draftId, brokerName, clientId, brokerFields);
    if (success) {
      setIsBrokerAdded(true);
      setLoadedClientId(clientId);
      // If the wizard itself claimed the proxy (no propProxyData provided), commit it here.
      // Capture IP and null the ref BEFORE await so intermediate re-renders can't
      // trigger accidental release via the proxy useEffect cleanup.
      if (!propProxyData && proxyDataRef.current) {
        const proxyIp = proxyDataRef.current.proxy_static_ip;
        setProxyData(null);
        proxyDataRef.current = null; // clear ref before await
        try {
          await apiClient.request({
            method: "post",
            url: `${server.godseye.baseUrl}proxies/commit`,
            data: { proxy_static_ip: proxyIp }
          });
        } catch (err) {
          console.error("Failed to commit proxy inside wizard:", err);
        }
      }

      // ── Save index multiplier to godseye (same API AccountView uses) ──────
      // Only for Optimus / PledgePlus products and brokers that use index options table
      const brokersThatNeedIndexMultiplier = ["fyers", "nubra", "dhan_hq"];
      const isOptimusOrPledgePlus = basicInfo.product_is_optimus || basicInfo.product_is_pledgeplus;
      if (isOptimusOrPledgePlus && brokersThatNeedIndexMultiplier.includes(brokerName)) {
        try {
          const multiplierPayload = {
            client_id: clientId,
            name,
            indexMultiplier: parseInt(brokerFields.multiplier ?? 0),
            broker: brokerName,
            bankniftyMultiplier: parseInt(brokerFields.banknifty_multiplier ?? 0),
            finniftyMultiplier: parseInt(brokerFields.finnifty_multiplier ?? 0),
            midcpniftyMultiplier: parseInt(brokerFields.midcpnifty_multiplier ?? 0),
            sensexMultiplier: parseInt(brokerFields.sensex_multiplier ?? 0),
          };
          await apiClient.post(
            `${server.godseye.baseUrl}add-index-options-multiplier`,
            multiplierPayload
          );
        } catch (err) {
          console.error("Failed to save index multiplier:", err);
          // Non-fatal — user is onboarded, multiplier can be set later
        }
      }

      toast.success(`${brokerName.toUpperCase()} user ${name} fully onboarded!`);
      if (brokerName === "fyers" && handleAddFyers) handleAddFyers(clientId, name);
      if (brokerName === "nubra" && handleAddNubra) handleAddNubra(clientId, name);
      if (brokerName === "dhan_hq" && handleAddDhanHQ) handleAddDhanHQ(clientId, name);
      if (brokerName === "zerodha" && handleAddZerodha) handleAddZerodha(clientId, name);
      if (brokerName === "mosl" && handleAddMOSL) handleAddMOSL(clientId, name);

      resetOnboarding();
      onClose(true);
    }
  };

  const activeBrokerInfo = BROKERS.find((b) => b.id === selectedBroker);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-5xl max-h-[92vh] mx-4 bg-[#0f1116] border border-zinc-800/60 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800/60 bg-[#0d0f18]">
          <div>
            <h2 className="text-white font-semibold text-lg flex items-center gap-2">
              Add New User
              {currentStep === 3 && selectedBroker && activeBrokerInfo && (
                <>
                  <span className="text-zinc-600 font-light">—</span>
                  <span
                    className={`text-sm font-semibold px-2.5 py-0.5 rounded-full ${activeBrokerInfo.bg} ${activeBrokerInfo.text} border ${activeBrokerInfo.border}`}
                  >
                    {activeBrokerInfo.label}
                  </span>
                </>
              )}
            </h2>
            <p className="text-zinc-500 text-xs mt-0.5">Step {currentStep} of {STEPS.length}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-zinc-800/60 text-zinc-500 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5" style={{ scrollbarWidth: "thin" }}>
          <StepIndicator currentStep={currentStep} />

          {currentStep === 1 && (
            <Step1BasicInfo data={basicInfo} onChange={handleBasicInfoChange} />
          )}
          {currentStep === 2 && (
            <Step2Legalities
              data={legalities}
              onChange={handleLegalitiesChange}
              basicInfo={basicInfo}
              draftId={draftId}
              loadDraft={loadDraft}
              saveManualDocId={saveManualDocId}
            />
          )}

          {currentStep === 3 && (
            <Step3BrokerDetails
              selectedBroker={selectedBroker}
              onBrokerChange={setSelectedBroker}
              allowedBrokers={allowedBrokers}
              proxyData={proxyData}
              handleBrokerSuccess={handleBrokerSuccess}
              onWizardSubmit={handleWizardSubmitForApproval}
              onClose={handleClose}
              isOnholdLegality={legalities.is_onhold_leegality}
              isLegalitiesComplete={legalities.is_legalities_complete}
              isBrokerAdded={isBrokerAdded}
              loadedClientId={loadedClientId}
              isPolarisOrPolarisLite={basicInfo.product_is_polaris || basicInfo.product_is_polaris_lite}
              basicInfo={basicInfo}
              draftData={draftData}
            />
          )}
        </div>

        {/* Footer Navigation */}
        <div className="px-6 py-4 border-t border-zinc-800/60 bg-[#0d0f18] flex items-center justify-between">
          {/* Back Button */}
          <button
            onClick={() => setCurrentStep((s) => s - 1)}
            disabled={currentStep === 1 || isSaving}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-zinc-800/60 text-zinc-300 hover:bg-zinc-700/60 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {/* Step Dots */}
          <div className="flex items-center gap-2">
            {STEPS.map((s) => (
              <div
                key={s.id}
                className={`h-2 rounded-full transition-all duration-300 ${s.id === currentStep
                  ? "bg-blue-500 w-5"
                  : s.id < currentStep
                    ? "bg-[#07F2C7] w-2"
                    : "bg-zinc-700 w-2"
                  }`}
              />
            ))}
          </div>

          {/* Next / Done */}
          {currentStep < 3 ? (
            <button
              onClick={handleNext}
              disabled={
                isSaving ||
                (currentStep === 1 && !isStep1Valid) ||
                (currentStep === 2 && !isStep2Valid)
              }
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all min-w-[100px] justify-center"
            >
              {isSaving ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Continue
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          ) : (
            <div className="w-24" />
          )}
        </div>
      </div>
    </div>
  );
}
